import { useState, useEffect } from "react";
import { Mail, HelpCircle } from "lucide-react";
import { Button, Icon, COLOURS } from "@shootsta/common-react";
import { openHubSpotConversationsWidget } from '@core/shared';
import moment from "moment-timezone";

export default function MaintenanceScreen({ maintenanceTimes }) {
  // State for progress percentage
  const [progress, setProgress] = useState(0);

  // State for time remaining
  const [timeRemaining, setTimeRemaining] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // State for estimated completion time
  const [estimatedCompletion, setEstimatedCompletion] = useState("");

  // Set up the maintenance timing
  useEffect(() => {
    const totalDuration = maintenanceTimes.maintenanceEnd - maintenanceTimes.maintenanceStart; // Total duration in seconds

    const calculateEstimatedTime = () => {
      const estimatedTime = moment.unix(maintenanceTimes.maintenanceEnd);
      return estimatedTime.format("hh:mm A");
    };

    // Set the estimated completion time
    setEstimatedCompletion(calculateEstimatedTime());

    // Set the start time
    const startTime = maintenanceTimes.maintenanceStart * 1000; // Convert to milliseconds

    // Update progress and time remaining every second
    const timer = setInterval(() => {
      const currentTime = new Date().getTime();
      const elapsedTime = (currentTime - startTime) / 1000; // elapsed time in seconds

      // Calculate progress percentage
      const progressPercentage = Math.min(100, (elapsedTime / totalDuration) * 100);
      setProgress(progressPercentage);

      // Calculate time remaining based on progress
      const remainingSeconds = totalDuration * (1 - progressPercentage / 100);
      const hours = Math.floor(remainingSeconds / 3600);
      const minutes = Math.floor((remainingSeconds % 3600) / 60);
      const seconds = Math.floor(remainingSeconds % 60);

      setTimeRemaining({ hours, minutes, seconds });

      // Clear interval when maintenance is complete
      if (progressPercentage >= 100) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [])

  // Format time with leading zeros
  const formatTime = (value: number) => value.toString().padStart(2, "0");

  return (
    <div
      className="flex items-center justify-center p-4 sm:p-6 md:p-8"
      style={{ backgroundColor: COLOURS.grey.athens.parthenon }}
    >
      <div
        className="rounded-lg max-w-3xl lg:max-w-4xl xl:max-w-6xl w-full p-6 sm:p-12 md:p-16 lg:p-20 shadow-lg border-0 overflow-hidden relative"
        style={{ backgroundColor: "white" }}
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gray-100">
          <div
            className="h-full transition-all duration-1000 ease-in-out"
            style={{
              width: `${progress}%`,
              backgroundColor: COLOURS.cyan.jungleGreen.monkey,
            }}
          />
        </div>

        <div className="space-y-8">
          {/* Header with SVG */}
          <div className="space-y-6 text-center">
            <div className="flex justify-center">
              <Icon name="shootstacolour" size="100" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ color: COLOURS.grey.denim }}>
                Scheduled Maintenance in Progress
              </h1>
              <p className="text-base sm:text-lg max-w-lg mx-auto" style={{ color: COLOURS.grey.regent }}>
                We're enhancing our platform with new features and improvements to provide you with a better experience.
              </p>
            </div>
          </div>

          {/* Maintenance details */}
          <div className="rounded-xl p-6 sm:p-8" style={{ backgroundColor: COLOURS.mono.ghostWhite }}>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Time information */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: COLOURS.grey.denim }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  Estimated Completion
                </h2>
                <p className="text-xl font-bold" style={{ color: COLOURS.grey.denim }}>
                  {estimatedCompletion}
                </p>
                <div className="space-y-2">
                  <div className="text-sm" style={{ color: COLOURS.grey.regent }}>
                    Progress: {Math.round(progress)}% complete
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-1000 ease-in-out"
                      style={{
                        width: `${progress}%`,
                        backgroundColor: COLOURS.cyan.jungleGreen.monkey,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Countdown */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold" style={{ color: COLOURS.grey.denim }}>
                  Time Remaining
                </h2>
                <div className="flex gap-4">
                  <div className="text-center">
                    <div
                      className="w-16 h-16 rounded-lg flex items-center justify-center text-xl font-bold transition-all duration-300"
                      style={{
                        backgroundColor: COLOURS.cyan.jungleGreen.monkeyLight,
                        color: COLOURS.cyan.jungleGreen.monkey,
                      }}
                    >
                      {formatTime(timeRemaining.hours)}
                    </div>
                    <div className="text-xs uppercase mt-2" style={{ color: COLOURS.grey.regent }}>
                      Hours
                    </div>
                  </div>
                  <div className="text-center">
                    <div
                      className="w-16 h-16 rounded-lg flex items-center justify-center text-xl font-bold transition-all duration-300"
                      style={{
                        backgroundColor: COLOURS.cyan.jungleGreen.monkeyLight,
                        color: COLOURS.cyan.jungleGreen.monkey,
                      }}
                    >
                      {formatTime(timeRemaining.minutes)}
                    </div>
                    <div className="text-xs uppercase mt-2" style={{ color: COLOURS.grey.regent }}>
                      Minutes
                    </div>
                  </div>
                  <div className="text-center">
                    <div
                      className="w-16 h-16 rounded-lg flex items-center justify-center text-xl font-bold transition-all duration-300"
                      style={{
                        backgroundColor: COLOURS.cyan.jungleGreen.monkeyLight,
                        color: COLOURS.cyan.jungleGreen.monkey,
                      }}
                    >
                      {formatTime(timeRemaining.seconds)}
                    </div>
                    <div className="text-xs uppercase mt-2" style={{ color: COLOURS.grey.regent }}>
                      Seconds
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Message */}
          <div
            className="text-center p-5 rounded-lg border border-dashed"
            style={{ borderColor: COLOURS.grey.mischka.russian }}
          >
            <p className="text-sm sm:text-base" style={{ color: COLOURS.grey.denim }}>
              We apologize for any inconvenience this maintenance may cause. Thank you for your patience as we work to
              improve our services for you. Your understanding is greatly appreciated.
            </p>
          </div>

          {/* Support Resources */}
          <div className="space-y-5">
            <h2 className="text-lg font-semibold text-center" style={{ color: COLOURS.grey.denim }}>
              Need immediate assistance?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Button
                className="flex items-center justify-center gap-2 w-full h-12 transition-transform duration-300 hover:scale-[1.02]"
                style={{
                  backgroundColor: COLOURS.cyan.jungleGreen.monkey,
                  color: "white",
                  border: "none",
                }}
                onClick={openHubSpotConversationsWidget}
              >
                <Mail className="w-4 h-4" />
                <span>Contact Support</span>
              </Button>
              <a
                href="https://help.shootsta.com/en/articles"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full"
              >
                <Button
                  variant="outline"
                  className="bg-white flex items-center justify-center gap-2 w-full h-12 transition-transform duration-300 hover:scale-[1.02] hover:bg-gray-100"
                  style={{
                    borderColor: COLOURS.cyan.jungleGreen.monkey,
                    color: COLOURS.cyan.jungleGreen.monkey,
                  }}
                >
                  <HelpCircle className="w-4 h-4" />
                  <span>Help Center</span>
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
